import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  CreateCampaignChallengeRequest,
  CreateCampaignChallengeResponse,
  CreatedCampaignChallengeRecord,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { unwrapGamePassEntityOrNull } from "./responseMappers";

/**
 * Create a challenge group under a campaign
 * (`POST /gamepass/v2/admin/challenges`). Campaigns reuse the season challenge
 * endpoint with `campaign_id` in place of `season_id` (see `ApiRoutes.js`).
 *
 * Tolerant unwrap (FE-1692 pattern): the POST already succeeded by the time we
 * parse the body, so a missing / unrecognised payload returns `null` rather
 * than surfacing a false creation failure. The orchestration flow treats a
 * `null` group id as "created without a resolvable id".
 */
export const postCreateCampaignChallenge = async (
  payload: CreateCampaignChallengeRequest,
): Promise<CreatedCampaignChallengeRecord | null> => {
  const response =
    await getPrivateInstance().post<CreateCampaignChallengeResponse>(
      API_ROUTES.CREATE_CHALLENGE,
      payload,
    );
  return unwrapGamePassEntityOrNull<CreatedCampaignChallengeRecord>(
    response.data,
    "challenge",
  );
};

export const useCreateCampaignChallenge = (): UseMutationResult<
  CreatedCampaignChallengeRecord | null,
  Error,
  CreateCampaignChallengeRequest
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "campaign-challenges", "create"],
    mutationFn: postCreateCampaignChallenge,
    // Mirror the update/delete hooks: a create must invalidate the campaign's
    // challenge + task lists (and its detail) so a later Details re-visit
    // re-hydrates from the server instead of serving the pre-create cache. Left
    // stale, the reverse mapper's name-based task fallback can attach a leftover
    // task from a same-named deleted challenge to the new one and mis-render its
    // task type.
    onSuccess: (_challenge, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "challenges", "list"],
      });
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "tasks", "list"],
      });
      if (variables.campaign_id) {
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "campaigns", "detail", variables.campaign_id],
        });
      }
    },
  });
};
